import { useEffect, useMemo, useState } from "react";
import { Search, X, Check, ChevronsUpDown, Filter, Activity, Heart, Bone, Eye, Smile, Brain, Baby, Sparkles, Scissors, Syringe, ShieldAlert } from "lucide-react";
import { PageShell, PageHero } from "@/components/knmc/PageShell";
import type { Doctor } from "@/hooks/useDoctors";
import { DoctorCard } from "@/components/knmc/DoctorCard";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Format department names to Title Case while preserving abbreviations like ENT, ICU, etc.
const PRESERVED_ABBREVIATIONS = new Set(["ENT", "ICU"]);
function formatDeptName(dept: string): string {
  if (!dept) return "";
  if (PRESERVED_ABBREVIATIONS.has(dept.toUpperCase())) {
    return dept.toUpperCase();
  }
  return dept
    .toLowerCase()
    .replace(/-+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// Retrieve Lucide Icon matching department characteristics
function getDeptIcon(dept: string) {
  const name = (dept || "").toLowerCase();
  if (name.includes("cardio")) return Heart;
  if (name.includes("neuro")) return Brain;
  if (name.includes("ortho") || name.includes("bone")) return Bone;
  if (name.includes("eye") || name.includes("ophthalm")) return Eye;
  if (name.includes("pediat") || name.includes("baby") || name.includes("paediat")) return Baby;
  if (name.includes("dent") || name.includes("smile")) return Smile;
  if (name.includes("surg") || name.includes("gastro") || name.includes("urology")) return Scissors;
  if (name.includes("anaesth") || name.includes("critical") || name.includes("icu")) return Syringe;
  if (name.includes("emerg")) return ShieldAlert;
  return Activity;
}

export default function DoctorsList({ doctors }: { doctors: Doctor[] }) {
  // Start with SSR-safe defaults so the first client render matches the
  // server-rendered HTML exactly (avoids React hydration mismatch / error #418).
  // URL params are applied a tick after mount instead.
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState<string>("all");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    const department = params.get("department");
    if (q) setQuery(q);
    if (department) setDept(department);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter out any invalid departments and sort them
  const deptOptions = useMemo(() => {
    const set = new Set(doctors.map((d) => d.department).filter(Boolean));
    return Array.from(set).sort();
  }, [doctors]);

  // Compute the top 6 departments by frequency (popularity) dynamically
  const popularDepts = useMemo(() => {
    const counts: Record<string, number> = {};
    doctors.forEach((d) => {
      if (d.department) {
        counts[d.department] = (counts[d.department] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name]) => name);
  }, [doctors]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return doctors
      .filter((d) => {
        const matchDept = dept === "all" || d.department === dept;
        if (!matchDept) return false;

        if (!q) return true;

        const hay = [
          d.practitioner_name,
          d.role,
          d.department,
          ...d.specialties,
          ...d.qualifications,
        ]
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a, b) => {
        const aHasImage = Boolean(a.image?.trim());
        const bHasImage = Boolean(b.image?.trim());

        // Doctors with images first
        if (aHasImage !== bHasImage) {
          return aHasImage ? -1 : 1;
        }

        // Alphabetical within each group
        return a.practitioner_name.localeCompare(b.practitioner_name);
      });
  }, [query, dept, doctors]);

  return (
    <PageShell>
      <PageHero
        eyebrow="Meet our specialists"
        title="Find the right doctor for you"
        description="Browse our consultants by department and book directly. All profiles include qualifications, availability, and patient ratings."
      />

      <section className="sticky top-20 z-30 px-4 md:px-6">
        <div className="mx-auto max-w-7xl rounded-3xl border border-border/60 bg-card/40 backdrop-blur-xl shadow-lg shadow-black/5 px-6 py-4 space-y-5">
          {/* Main search and select row */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Search Input Container */}
            <div className="relative flex-1 max-w-lg">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by doctor name, specialty, qualification..."
                className="h-12 w-full rounded-2xl border border-border/60 bg-background/60 backdrop-blur-sm pl-11 pr-11 text-sm text-foreground placeholder:text-muted-foreground shadow-sm outline-none transition-all hover:border-border focus:border-primary focus:ring-2 focus:ring-primary/10 focus:bg-background"
                aria-label="Search doctors"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Department Search/Selector Dropdown */}
            <div className="flex items-center gap-3">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="h-12 w-full md:w-72 justify-between rounded-2xl border-border/60 bg-background/60 backdrop-blur-sm px-4 text-sm shadow-sm hover:bg-background/80"
                  >
                    <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
                      <Filter className="h-4 w-4 shrink-0 text-primary/75" />
                      <span className="truncate text-foreground font-medium">
                        {dept === "all" ? "All Departments" : formatDeptName(dept)}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 rounded-2xl shadow-elevated border-border/85" align="end">
                  <Command>
                    <CommandInput placeholder="Search department..." className="h-11 border-none" />
                    <CommandList className="max-h-80">
                      <CommandEmpty>No department found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setDept("all");
                            setOpen(false);
                          }}
                          className="rounded-lg py-2.5 px-3"
                        >
                          <Check className={cn("mr-2 h-4 w-4 shrink-0 text-primary", dept === "all" ? "opacity-100" : "opacity-0")} />
                          <span className="truncate font-semibold">All Departments</span>
                        </CommandItem>
                        {deptOptions.map((opt) => (
                          <CommandItem
                            key={opt}
                            value={opt}
                            onSelect={() => {
                              setDept(opt);
                              setOpen(false);
                            }}
                            title={formatDeptName(opt)}
                            className="rounded-lg py-2.5 px-3"
                          >
                            <Check className={cn("mr-2 h-4 w-4 shrink-0 text-primary", dept === opt ? "opacity-100" : "opacity-0")} />
                            <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                              {formatDeptName(opt)}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {(dept !== "all" || query) && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDept("all");
                    setQuery("");
                  }}
                  className="h-12 px-4 rounded-2xl text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-destructive hover:bg-destructive/5"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Counts bar */}
          <div className="flex items-center justify-between border-t border-border/50 pt-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">
            <div>
              Showing <span className="font-semibold text-primary">{filtered.length}</span> of{" "}
              <span className="font-semibold">{doctors.length}</span> specialists
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6">
          {filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border/80 bg-card p-16 text-center shadow-soft">
              <h3 className="font-display text-2xl font-semibold text-primary">No doctors found</h3>
              <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
                We couldn't find any specialist matching your search criteria. Try typing a different keyword or resetting filters.
              </p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setDept("all");
                }}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/95 transition-all"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {filtered.map((d) => (
                <DoctorCard key={d.practitioner_name} d={d} />
              ))}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}